import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import BrandLogo from "./BrandLogo";

const DEFAULT_STEPS = ["Describe", "Clarify", "Confirm"];

export default function WizardLayout({
  stepIndex,
  totalSteps,
  stepLabels = DEFAULT_STEPS,
  eyebrow,
  title,
  subtitle,
  children,
  footer,
}) {
  const pct = Math.round(((stepIndex + 1) / totalSteps) * 100);
  const labels = stepLabels.slice(0, totalSteps);

  return (
    <div className="min-h-screen w-full grid grid-cols-1 lg:grid-cols-12 wizard-surface">
      <aside className="relative lg:col-span-4 xl:col-span-4 lg:min-h-screen overflow-hidden bg-[hsl(var(--brand-navy))]">
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--brand-navy-dark))] to-[hsl(var(--brand-navy-mid))]" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[hsl(var(--brand-navy))] via-[hsl(var(--brand-red))] to-[hsl(var(--brand-navy))]" />

        <div className="relative z-10 p-8 lg:p-12 xl:p-16 flex flex-col h-full min-h-[280px] lg:min-h-screen text-white">
          <Link
            to="/"
            className="inline-flex bg-white rounded-xl px-4 py-2.5 shadow-lg shadow-black/20 w-fit transition-all duration-300 hover:shadow-glow-red hover:-translate-y-0.5"
          >
            <BrandLogo to={null} variant="compact" />
          </Link>

          <div className="mt-16 lg:mt-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-[10px] uppercase tracking-[0.24em] text-white/85 font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--brand-red))]" />
              {eyebrow || `Step ${stepIndex + 1} of ${totalSteps}`}
            </div>
            <h1 className="mt-5 text-3xl lg:text-4xl xl:text-5xl font-light tracking-tight leading-[1.05]">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-6 text-white/75 leading-relaxed max-w-md text-base">{subtitle}</p>
            )}
          </div>

          <div className="mt-10 lg:mt-16">
            <div className="flex items-center justify-between text-xs text-white/60 mb-3">
              <span className="uppercase tracking-[0.24em]">Progress</span>
              <span className="font-mono tabular-nums">{pct}%</span>
            </div>
            <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[hsl(var(--brand-navy-mid))] via-[hsl(var(--brand-red))] to-[hsl(var(--brand-red-light))] transition-[width] duration-500 ease-out shadow-[0_0_14px_hsla(358,81%,49%,0.45)]"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-6 flex flex-col gap-3">
              {labels.map((label, i) => {
                const done = i < stepIndex;
                const active = i === stepIndex;
                return (
                  <div key={label} className="flex items-center gap-3">
                    <span
                      className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold shrink-0 transition-all duration-300 ${
                        done
                          ? "bg-[hsl(var(--brand-red))] text-white"
                          : active
                            ? "bg-white text-[hsl(var(--brand-navy))] ring-2 ring-[hsl(var(--brand-red))] scale-110"
                            : "bg-white/10 text-white/50 border border-white/15"
                      }`}
                    >
                      {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : i + 1}
                    </span>
                    <span
                      className={`text-sm transition-colors ${active ? "text-white font-medium" : done ? "text-white/75" : "text-white/40"}`}
                    >
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-auto pt-16 hidden lg:block">
            <div className="rounded-xl bg-white/5 border border-white/10 p-5 backdrop-blur-sm">
              <div className="flex items-center gap-2">
                <span className="accent-line w-6" />
                <span className="text-xs uppercase tracking-[0.24em] text-white/50">
                  Trusted procurement
                </span>
              </div>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">
                Verified Turkish manufacturers · Fast turnaround · Zero registration required
              </p>
            </div>
          </div>
        </div>
      </aside>

      <main className="lg:col-span-8 xl:col-span-8 flex flex-col min-h-screen">
        <div className="flex-1 py-10 lg:py-16 px-6 sm:px-10 lg:px-16 xl:px-20">
          <div className="max-w-2xl mx-auto lg:mx-0">{children}</div>
        </div>
        {footer && (
          <div className="sticky bottom-0 border-t border-neutral-200/80 bg-white/95 backdrop-blur-md px-6 sm:px-10 lg:px-16 xl:px-20 py-5 shadow-[0_-4px_24px_-8px_rgba(11,33,71,0.08)]">
            <div className="max-w-2xl mx-auto lg:mx-0">{footer}</div>
          </div>
        )}
      </main>
    </div>
  );
}
